import * as React from 'react';
import { FC, ReactElement, useState } from 'react';
import { Table } from '@utmb/design-system/table/Table';
import RaceTrackPointView from '../../../types/view/RaceTrackPointView';
import { RaceTrackIcon } from '../RaceTrackIcon';
import { getPointIcons } from '@utmb/utils/point';
import styles from './race-time-charts.module.scss';
import { useRaceTrack } from '@utmb/contexts/RaceTrackContext';
import { useTranslation } from 'next-i18next';
import RaceProfileDialog from '@utmb/features/race-track/profile/RaceProfileDialog';
import { usePreference } from '@utmb/contexts/PreferenceContext';
import { UnitEnum } from '@utmb/types/unit-enum';
import { formatUnit } from '@utmb/utils/unit';
import isMobile from '@utmb/utils/is-mobile';

export const RaceTimeCharts: FC<{ hideSlowest: boolean }> = ({ hideSlowest }) => {
    const {
        track: { points },
    } = useRaceTrack();

    const {
        preferences: { preferredUnit },
    } = usePreference();

    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [dialogPoint, setDialogPoint] = useState<RaceTrackPointView>(points[0]);

    const { t } = useTranslation();

    const tableFormat = [
        { Header: t('raceTrack.timeCharts.point'), accessor: 'name', width: 120 },
        { Header: `${t('raceTrack.timeCharts.elevation')} (${preferredUnit === UnitEnum.IMPERIAL ? 'ft' : 'M'})`, accessor: 'elevation', width: 70 },
        { Header: `${t('raceTrack.timeCharts.distance')} (${preferredUnit === UnitEnum.IMPERIAL ? 'miles' : 'km'})`, accessor: 'distanceTotal', width: 50 },
        {
            Header: `${t('raceTrack.timeCharts.distanceInterval')} (${preferredUnit === UnitEnum.IMPERIAL ? 'miles' : 'km'})`,
            accessor: 'distanceFromLastPoint',
            width: 40,
        },
        {
            Header: `${t('raceTrack.timeCharts.elevationGainPlus')} (${preferredUnit === UnitEnum.IMPERIAL ? 'ft' : 'M'})`,
            accessor: 'gainElevation',
            width: 60,
        },
        {
            Header: `${t('raceTrack.timeCharts.elevationGainMinus')} (${preferredUnit === UnitEnum.IMPERIAL ? 'ft' : 'M'})`,
            accessor: 'lossElevation',
            width: 60,
        },
        { Header: t('raceTrack.timeCharts.fastest'), accessor: 'fastest', width: 80, cssPostFix: 'RankCat' },
        { Header: t('raceTrack.timeCharts.slowest'), accessor: 'slowest', width: 80, cssPostFix: 'Time' },
        { Header: t('raceTrack.timeCharts.cutOff'), accessor: 'cutoff', width: 130, cssPostFix: 'Diff' },
        { Header: t('raceTrack.timeCharts.services'), accessor: 'services', width: 50, cssPostFix: 'Performance' },
    ];
    if (hideSlowest) {
        tableFormat.splice(7, 1);
        if (!isMobile()) tableFormat[0].width = 180;
    }

    const tableData = points.map((point: RaceTrackPointView) => {
        const icons = getPointIcons(point);

        const handleServiceClick = (): void => {
            setIsDialogOpen(true);
            setDialogPoint(point);
        };

        const getServiceIcons = (icons: any): ReactElement | undefined => {
            if (!icons.length) {
                return undefined;
            }

            if (icons.length === 1) {
                return (
                    <div className={styles.badge_icons_single}>
                        <span key={icons[0]} className={styles.icon}>
                            <RaceTrackIcon icon={icons[0]} color="var(--utmb-color-race-primary-darken)" />
                        </span>
                    </div>
                );
            } else if (icons.length === 2) {
                return (
                    <div className={styles.badge_icons} aria-hidden="true" onClick={handleServiceClick}>
                        {icons.map((icon: any) => (
                            <span key={icon} className={styles.icon}>
                                <RaceTrackIcon icon={icon} color="var(--utmb-color-race-primary-darken)" />
                            </span>
                        ))}
                    </div>
                );
            } else if (icons.length > 2) {
                return (
                    <div className={styles.badge_icons}>
                        <span key={icons[0]} className={styles.icon} aria-hidden="true" onClick={handleServiceClick}>
                            <RaceTrackIcon icon={icons[0]} color="var(--utmb-color-race-primary-darken)" />{' '}
                            <span className={styles.icon_qty}>+{icons.length - 1}</span>
                        </span>
                    </div>
                );
            }
        };

        const { distanceFromLastPoint, distance, elevation, gainElevation, gainElevationFromLastPoint, lossElevation, lossElevationFromLastPoint, ...rest } =
            point;

        return {
            distanceTotal: formatUnit(preferredUnit, 'distance', distance / 1000, UnitEnum.METRIC, true),
            distanceFromLastPoint: formatUnit(preferredUnit, 'distance', distanceFromLastPoint / 1000, UnitEnum.METRIC, true),
            elevation: formatUnit(preferredUnit, 'elevation', elevation, UnitEnum.METRIC, true),
            gainElevation: formatUnit(preferredUnit, 'elevationGain', gainElevation, UnitEnum.METRIC, true),
            gainElevationFromLastPoint: formatUnit(preferredUnit, 'elevationGain', gainElevationFromLastPoint, UnitEnum.METRIC, true),
            lossElevation: formatUnit(preferredUnit, 'elevationLoss', lossElevation, UnitEnum.METRIC, true),
            lossElevationFromLastPoint: formatUnit(preferredUnit, 'elevationLoss', lossElevationFromLastPoint, UnitEnum.METRIC, true),
            ...rest,
            services: getServiceIcons(icons),
        };
    });

    return (
        <div className={styles.table_placeholder}>
            <div className={styles.table_container}>
                {/* Waiting designs */}
                {/* <div>
                    {t('raceTrack.distance')}: <b>{roundToDistanceInKmDecimal(distance)}</b> km
                </div>
                <div>
                    {t('raceTrack.elevationMax')}: <b>{maxElevation}</b> M
                </div>
                <div>
                    {t('raceTrack.elevationMin')}: <b>{minElevation}</b> M
                </div>
                <div>
                    {t('raceTrack.elevationGainPositive')}:<b>{gainElevation}</b> MT+
                </div>
                <div>
                    {t('raceTrack.elevationGainNegative')}:<b>{lossElevation}</b> MT+
                </div>
                <br />
                <br />
                <br /> */}
                <Table tableFormat={tableFormat} tableData={tableData} />
                <RaceProfileDialog isOpen={isDialogOpen} close={() => setIsDialogOpen(false)} point={dialogPoint} />
            </div>
        </div>
    );
};
